import { z } from "zod";
import { BracketSchema } from "./bracket.js";
// A program-specific feedback question, as asked of ONE age bracket (Relationships-Australia-WA/Services-Delivery-Technology-Upgrade#267).
//
// `id` is namespaced (prog_<program>_<key>) and doubles as the answer key. Repeating
// it across brackets is safe because a form is served for a single bracket, so only
// one row per id ever reaches it.
//
// `bracket` is stated, never defaulted: a write that omitted it would silently
// target the adult row, which is the one most likely to already carry wording
// someone relies on.
export const ProgramQuestionSchema = z.object({
    programId: z.string(),
    id: z.string(),
    bracket: BracketSchema,
    label: z.string().max(200),
    // Where this question sits among the others asked of the same program+bracket,
    // ascending, lowest first (Relationships-Australia-WA/Services-Delivery-Technology-Upgrade#398,
    // the FAIR form). Null/absent (most programs) means no
    // fixed position: the composer places it randomly among the other unordered
    // questions it draws, after any ordered ones. Ties (two questions sharing a
    // value) are broken by id — not rejected, since a program author picking the
    // same number twice is a minor authoring slip, not a state worth blocking on.
    order: z.number().int().positive().nullable().optional(),
});
// The write body for PUT /api/admin/program-questions/:id — the URL owns the id.
export const UpsertProgramQuestionSchema = ProgramQuestionSchema.omit({
    id: true,
});
// A GET /api/admin/program-questions row: a stored question plus its program's
// display name, resolved from the local ICIS program mirror. `programName` is
// null when the program isn't in the mirror (so the client falls back to the id).
// Read-only enrichment — the write shape stays ProgramQuestion / Upsert…
export const ProgramQuestionRowSchema = ProgramQuestionSchema.extend({
    programName: z.string().nullable(),
});
export const ProgramQuestionsResponseSchema = z.object({
    programQuestions: ProgramQuestionRowSchema.array(),
});
